import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';
import YouTube from 'react-youtube';
import { useEffect, useRef, useState } from 'react';
import useSizeClass from 'hooks/use-size-class';
import MuteIcon from 'components/icon/mute-icon';
import VolumeIcon from 'components/icon/volume-icon';
import Image from 'next/image';

interface VideoCardProps {
  index: number;
  videoId: string;
  bottomText: string;
  cardType: 'primary' | 'secondary' | 'tertiary';
  isActiveDesktop: boolean;
  setActiveDesktop: (index: number) => void;
  isActiveMobile: boolean;
  isMobileFullScreen: boolean;
  setFullScreenIndex: (index: number) => void;
  shouldTranslate: boolean;
}

export default function VideoCard({ index, videoId, bottomText, cardType, isActiveDesktop = false, setActiveDesktop, isActiveMobile = false, isMobileFullScreen, setFullScreenIndex, shouldTranslate }: VideoCardProps) {
  const playerRef = useRef(null);
  const isMobile = useSizeClass({ max: 675 });
  const [isMuted, setIsMuted] = useState(true);
  const [isPaused, setIsPaused] = useState(true);
  const [showDesktopMuteButton, setShowDesktopMuteButton] = useState(false);
  const [loadVideo, setLoadVideo] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  // Used to determine if player has started playing before showing it, so users don't see initial red play button
  const [hasStarted, setHasStarted] = useState(false);

  const handleClick = () => {
    if (!playerReady) return;
    if (playerRef?.current) {
      if (isMobile) {
        handleClickMobile();
        return;
      }
      if (isMuted) {
        playerRef.current.unMute();
        setIsMuted(!isMuted);
      }

      if (isActiveDesktop) {
        setActiveDesktop(null);
        playerRef.current.pauseVideo();
      }
      else {
        setActiveDesktop(index);
        playerRef.current.playVideo();
      }
    }
  };

  const handleClickMobile = () => {
    if (!isMobile || !playerReady) return;
    
    if (isMobileFullScreen) { 
      if (isPaused) {
        playerRef.current.playVideo();
        setIsPaused(false);
      }
      else {
        playerRef.current.pauseVideo();
        setIsPaused(true);
      }
    }
    else {
      setFullScreenIndex(index);
      setIsMuted(false);
      playerRef.current.unMute();
    }
  };

  const handleClickMute = () => {
    if (isMuted) {
      playerRef.current.unMute();
    }
    else {
      playerRef.current.mute();
    }
    setIsMuted(!isMuted);
  };
  
  const onPlayerReady = event => {
    playerRef.current = event.target;
    playerRef.current.playVideo();
    setIsPaused(false); 
    setPlayerReady(true);
    if (!isMobile) {
      setShowDesktopMuteButton(true);
    }
  };

  const handleStateChange = event => {
    if (event.data === 1 && !hasStarted) {
      setHasStarted(true);
    }
  };

  const handleMouseEnter = () => {
    if (!loadVideo) {
      setLoadVideo(true);
    }
    if (playerRef?.current && !isMobile) {
      playerRef.current.playVideo();
      setIsPaused(false);
      setShowDesktopMuteButton(true);
    }
  };

  const handleMouseLeave = () => {
    if (playerRef.current && !isActiveDesktop && !isMobile) {
      playerRef.current.seekTo(0, false); 
      playerRef.current.pauseVideo();
      setIsPaused(true);
      setShowDesktopMuteButton(false);
    }
  };

  const onPlayerEnd = () => {
    if (playerRef.current) {
      playerRef.current.seekTo(0, true);
    }
  };

  const opts = {
    playerVars: {
      autoplay: 0,
      controls: 0,
      showinfo: 0,
      modestbranding: 1,
      rel: 0,
      mute: 1,
    },
  };

  useEffect(() => {
    if (isActiveMobile && isMobile) {
      if (!loadVideo) {
        setLoadVideo(true);
      }
      if (playerRef?.current) {
        playerRef.current.playVideo();
        setIsPaused(false);
      }
    }
    else if (!isActiveMobile && isMobile && loadVideo) {
      if (playerRef?.current) {
        playerRef.current.pauseVideo();
        setIsPaused(true);
      }
    }
   
  }, [isActiveMobile, isMobile, loadVideo]);

  useEffect(() => {
    if (!isActiveDesktop && playerRef?.current && !isMobile) {
      playerRef.current.pauseVideo();
      setIsPaused(true);
      setIsMuted(true);
      playerRef.current.mute();
    }
  }, [isActiveDesktop, isMobile]);

  const MuteButton = ({ onClick }: { onClick: () => void }) => {
    const [isHovered, setIsHovered] = useState(false);
  
    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);
  
    const iconFillColor = isHovered ? '#CECFD3' : 'white';
  
    return (
      <button className={styles['mute-button']}
        onClick={() => {
          onClick();
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {!isMuted ? <MuteIcon fill={iconFillColor} /> : <VolumeIcon fill={iconFillColor}/>}
      </button>
    );
  };
  
  return (
    <>
      <div className={buildClassName(styles.component, styles[`${cardType}-color`], shouldTranslate && styles[`${cardType}-transform`], isMobile && isMobileFullScreen && styles['full-screen'])} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <div className={styles.wrapper} onClick={handleClick}>
          <div className={styles.video}>
            {loadVideo && 
              <div className={playerReady ? '' : styles['hidden']}>
                <YouTube
                  videoId={videoId}
                  opts={opts}
                  onReady={onPlayerReady}
                  onEnd={onPlayerEnd}
                  onStateChange={handleStateChange}
                />
              </div>
            }
            {(!loadVideo || !playerReady || !hasStarted) && <Image src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`} alt='YouTube video thumbnail' fill />}
          </div>
          <div className={buildClassName(styles['bottom-text'], styles[`${cardType}-color`])}>{bottomText}</div>
        </div>
        {showDesktopMuteButton && !isMobile && <MuteButton onClick={handleClickMute} />}
      </div>
      {isMobileFullScreen && isMobile && <MuteButton onClick={handleClickMute} />}
    </>
  );
}
